import { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';

const DataManagementSection = () => {
  const [exportOptions, setExportOptions] = useState({
    analytics: true,
    content: true,
    campaigns: false,
    comments: false
  });
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');

  const exportCategories = [
    {
      key: 'analytics',
      title: 'Analytics Data',
      description: 'Performance metrics, engagement stats, and growth reports',
      icon: 'BarChart3',
      size: '2.4 MB'
    },
    {
      key: 'content',
      title: 'Content Library',
      description: 'All scheduled and published posts with media files',
      icon: 'FileText',
      size: '15.8 MB'
    },
    {
      key: 'campaigns',
      title: 'Campaign Data',
      description: 'Campaign details, performance, and targeting information',
      icon: 'Target',
      size: '1.2 MB'
    },
    {
      key: 'comments',
      title: 'Comment History',
      description: 'All automated and manual comments with engagement data',
      icon: 'MessageSquare',
      size: '3.6 MB'
    }
  ];

  const handleExportData = () => {
    const selectedData = Object.keys(exportOptions)?.filter(key => exportOptions?.[key]);
    console.log('Exporting data:', selectedData);
  };

  const handleDeleteAccount = () => {
    if (deleteConfirmText === 'DELETE MY ACCOUNT') {
      console.log('Account deletion confirmed');
      setShowDeleteConfirm(false);
      setDeleteConfirmText('');
    }
  };

  const getTotalSize = () => {
    return exportCategories?.filter(cat => exportOptions?.[cat?.key])?.reduce((total, cat) => {
        const size = parseFloat(cat?.size);
        return total + size;
      }, 0)?.toFixed(1);
  };

  return (
    <div className="space-y-6">
      <div className="bg-card rounded-lg border border-border p-6">
        <div className="mb-6">
          <h2 className="text-xl font-semibold text-foreground">Export Your Data</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Download a copy of your data for backup or migration purposes
          </p>
        </div>

        <div className="space-y-3 mb-6">
          {exportCategories?.map((category) => (
            <div 
              key={category?.key}
              className="flex items-start gap-4 p-4 border border-border rounded-lg hover:border-primary/50 transition-colors"
            >
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                <Icon name={category?.icon} size={20} className="text-primary" />
              </div>
              <div className="flex-1">
                <Checkbox
                  label={category?.title}
                  description={category?.description}
                  checked={exportOptions?.[category?.key]}
                  onChange={(e) => setExportOptions(prev => ({
                    ...prev,
                    [category?.key]: e?.target?.checked
                  }))}
                />
              </div>
              <span className="text-xs text-muted-foreground font-medium">
                {category?.size}
              </span>
            </div>
          ))}
        </div>

        <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg border border-border mb-4">
          <div>
            <p className="text-sm font-medium text-foreground">Total Export Size</p>
            <p className="text-xs text-muted-foreground mt-0.5">
              Selected {Object.values(exportOptions)?.filter(Boolean)?.length} of {exportCategories?.length} categories
            </p>
          </div>
          <span className="text-lg font-semibold text-primary">
            {getTotalSize()} MB
          </span>
        </div>

        <div className="flex gap-3">
          <Button 
            variant="default" 
            iconName="Download" 
            iconPosition="left"
            onClick={handleExportData}
            disabled={!Object.values(exportOptions)?.some(Boolean)}
          >
            Export Selected Data
          </Button>
          <Button 
            variant="outline"
            onClick={() => setExportOptions({
              analytics: true,
              content: true,
              campaigns: true,
              comments: true
            })}
          >
            Select All
          </Button>
        </div>

        <div className="mt-6 p-4 bg-primary/5 rounded-lg border border-primary/20">
          <div className="flex items-start gap-3">
            <Icon name="Info" size={20} className="text-primary flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-foreground mb-1">
                Data Export Information
              </p>
              <ul className="text-xs text-muted-foreground space-y-1">
                <li>• Your data will be exported in JSON format for easy portability</li>
                <li>• Media files will be included as downloadable links</li>
                <li>• Export process may take a few minutes depending on data size</li>
                <li>• You'll receive an email when your export is ready to download</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
      <div className="bg-card rounded-lg border border-border p-6">
        <div className="mb-6">
          <h2 className="text-xl font-semibold text-foreground">Data Retention Policy</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Understand how we store and manage your data
          </p>
        </div>

        <div className="space-y-4">
          <div className="flex items-start gap-3 p-4 border border-border rounded-lg">
            <Icon name="Database" size={20} className="text-primary flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="text-sm font-semibold text-foreground mb-1">Active Data Storage</h3>
              <p className="text-xs text-muted-foreground">
                All your content, analytics, and campaign data is stored securely while your account is active. We use industry-standard encryption to protect your information.
              </p>
            </div>
          </div>

          <div className="flex items-start gap-3 p-4 border border-border rounded-lg">
            <Icon name="Archive" size={20} className="text-primary flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="text-sm font-semibold text-foreground mb-1">Archived Content</h3>
              <p className="text-xs text-muted-foreground">
                Deleted posts and campaigns are archived for 30 days before permanent deletion. You can restore archived content during this period.
              </p>
            </div>
          </div>

          <div className="flex items-start gap-3 p-4 border border-border rounded-lg">
            <Icon name="Clock" size={20} className="text-primary flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="text-sm font-semibold text-foreground mb-1">Analytics History</h3>
              <p className="text-xs text-muted-foreground">
                Performance data and analytics are retained for 24 months. Older data is aggregated and anonymized for platform improvement purposes.
              </p>
            </div>
          </div>
        </div>
      </div>
      <div className="bg-card rounded-lg border border-destructive p-6">
        <div className="mb-6">
          <h2 className="text-xl font-semibold text-destructive">Delete Account</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Permanently delete your account and all associated data
          </p>
        </div>

        <div className="p-4 bg-destructive/5 rounded-lg border border-destructive/20 mb-6">
          <div className="flex items-start gap-3">
            <Icon name="AlertTriangle" size={20} className="text-destructive flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-foreground mb-2">
                Warning: This action cannot be undone
              </p>
              <ul className="text-xs text-muted-foreground space-y-1">
                <li>• All your content, campaigns, and analytics will be permanently deleted</li>
                <li>• Connected social media accounts will be disconnected</li>
                <li>• Team members will lose access to shared resources</li>
                <li>• Active subscriptions will be cancelled immediately</li>
                <li>• You will not be able to recover your data after deletion</li>
              </ul>
            </div>
          </div>
        </div>

        {!showDeleteConfirm ? (
          <Button 
            variant="destructive" 
            iconName="Trash2" 
            iconPosition="left"
            onClick={() => setShowDeleteConfirm(true)}
          >
            Delete My Account
          </Button>
        ) : (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Type "DELETE MY ACCOUNT" to confirm
              </label>
              <input
                type="text"
                value={deleteConfirmText}
                onChange={(e) => setDeleteConfirmText(e?.target?.value)}
                className="w-full px-3 py-2 bg-background border border-input rounded-lg text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-destructive"
                placeholder="DELETE MY ACCOUNT"
              />
            </div>
            <div className="flex gap-3">
              <Button 
                variant="destructive"
                onClick={handleDeleteAccount}
                disabled={deleteConfirmText !== 'DELETE MY ACCOUNT'}
              >
                Confirm Deletion
              </Button>
              <Button 
                variant="outline"
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setDeleteConfirmText('');
                }}
              >
                Cancel
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DataManagementSection;